import { useEffect, useState } from "react"
import axiosInstance from "../utils/axiosConfig.ts"
import { getAssetUrl } from "../utils/auth.ts"
import {
  getDefaultLoginCompanyName,
  getDefaultLoginFavicon,
  PublicLoginBranding,
} from "../utils/loginBranding.ts"

const NEUTRAL_FAVICON = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E"

export function updatePageFavicon(faviconUrl: string) {
  let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']")
  if (!link) {
    link = document.createElement("link")
    link.rel = "shortcut icon"
    document.getElementsByTagName("head")[0].appendChild(link)
  }
  link.href = faviconUrl
}

function buildDefaultBranding(): PublicLoginBranding {
  return {
    configured: false,
    name: getDefaultLoginCompanyName(),
    logo_url: null,
    favicon_url: getDefaultLoginFavicon(),
  }
}

/**
 * Resolve company logo/name from vendor domain via GET /public/brand
 * (same contract as the login and marketing landing pages).
 */
export function usePublicLoginBranding(pageTitleSuffix?: string) {
  const [branding, setBranding] = useState<PublicLoginBranding | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let isMounted = true
    const defaultBranding = buildDefaultBranding()

    if (pageTitleSuffix) {
      document.title = pageTitleSuffix
    }
    updatePageFavicon(NEUTRAL_FAVICON)

    const fetchBranding = async () => {
      try {
        const response = await axiosInstance.get("/public/brand", {
          skipErrorToast: true,
        } as Record<string, unknown>)
        if (!isMounted) return

        const payload = response.data || {}
        const configured = Boolean(payload.configured)
        const resolved: PublicLoginBranding = configured
          ? {
              configured: true,
              host: payload.host,
              id: payload.id,
              name: payload.name || defaultBranding.name,
              tagline: payload.tagline || "",
              logo_url: getAssetUrl(payload.logo_url),
              favicon_url: getAssetUrl(payload.favicon_url) || defaultBranding.favicon_url,
            }
          : defaultBranding

        setBranding(resolved)
      } catch {
        if (!isMounted) return
        setBranding(defaultBranding)
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    fetchBranding()

    return () => {
      isMounted = false
    }
  }, [pageTitleSuffix])

  useEffect(() => {
    if (!branding) return
    const title = pageTitleSuffix
      ? `${branding.name} - ${pageTitleSuffix}`
      : branding.name
    document.title = title
    updatePageFavicon(branding.favicon_url || getDefaultLoginFavicon())
  }, [branding, pageTitleSuffix])

  return {
    branding,
    loading,
    companyName: branding?.name ?? getDefaultLoginCompanyName(),
    logoUrl: branding?.logo_url ?? null,
  }
}
